import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Presentation-only purchase enhancement. The native product form owns the one
 * cart request; its standard event promise is the only success signal.
 * The parent Component owns this controller's connection and cleanup.
 */
export class SecurPurchaseController {
  #root;
  #events;
  #active = false;
  #form;
  #productForm;
  #mainButton;
  #sticky;
  #stickyButton;
  #template;
  #motion;
  #purchaseObserver;
  #buttonObserver;
  #stickyVisible;
  #stickyFrame;
  #stickyTimer;
  #confirmationTimer;
  #feedbackTimer;
  #pending;
  #latest;
  #disabledBeforeRequest = false;
  #previousPuppet;

  /** @param {HTMLElement} root */
  constructor(root) {
    this.#root = root;
  }

  connect() {
    this.#form = document.getElementById(this.#root.dataset.formId);
    if (!(this.#form instanceof HTMLFormElement)) return;
    this.#mainButton = this.#form.querySelector('button[type="submit"]');
    if (!(this.#mainButton instanceof HTMLButtonElement)) return;
    this.#productForm = this.#form.closest('product-form-component');
    if (!this.#productForm || !this.#root.contains(this.#productForm)) return;

    this.#active = true;
    this.#productForm.dataset.scConfirmedPurchase = '';
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.#sticky = this.#root.querySelector('[data-sc-sticky]');
    this.#stickyButton = this.#root.querySelector('[data-sc-buy]');
    this.#template = this.#root.querySelector('[data-sc-purchase-feedback]');
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Native click feedback is optimistic. Keep its request/validation handlers,
    // and supply the visual confirmation after the server promise settles.
    this.#previousPuppet = this.#mainButton.getAttribute('data-puppet');
    this.#mainButton.dataset.puppet = 'true';
    this.#mainButton.removeAttribute('data-added');
    for (const button of this.#buttons) {
      if (!(this.#template instanceof HTMLTemplateElement)) continue;
      const feedback = this.#template.content.firstElementChild?.cloneNode(true);
      if (!feedback) continue;
      button.dataset.scPurchaseEnhanced = '';
      button.append(feedback);
    }

    this.#root.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, options);
    this.#root.addEventListener(StandardEvents.cartError, this.#handleCartError, options);
    this.#stickyButton?.addEventListener('click', () => {
      if (this.#pending || this.#mainButton.disabled) return;
      if (!this.#form.checkValidity()) {
        this.#form.reportValidity();
        return;
      }
      this.#form.requestSubmit(this.#mainButton);
    }, options);
    this.#form.addEventListener('submit', (event) => {
      if (!this.#pending) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { ...options, capture: true });

    this.#buttonObserver = new MutationObserver(() => this.#syncDisabled());
    this.#buttonObserver.observe(this.#mainButton, { attributes: true, attributeFilter: ['disabled'] });
    this.#syncDisabled();

    if (this.#sticky) {
      this.#purchaseObserver = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        // Separate thresholds avoid toggling repeatedly at a single scroll pixel.
        if (entry.intersectionRatio >= 0.35) this.#setStickyVisible(false);
        else if (entry.intersectionRatio <= 0.05) this.#setStickyVisible(true);
        else if (this.#stickyVisible === undefined) this.#setStickyVisible(!entry.isIntersecting);
      }, { threshold: [0, 0.05, 0.35] });
      this.#purchaseObserver.observe(this.#mainButton);
      this.#motion.addEventListener('change', () => {
        if (!this.#motion.matches) return;
        cancelAnimationFrame(this.#stickyFrame);
        clearTimeout(this.#stickyTimer);
        this.#sticky.hidden = !this.#stickyVisible;
        this.#sticky.dataset.scMotion = this.#stickyVisible ? 'visible' : 'hidden';
      }, options);
    }
  }

  get #buttons() {
    return [this.#mainButton, this.#stickyButton].filter((button) => button instanceof HTMLButtonElement);
  }

  #syncDisabled() {
    if (this.#pending && !this.#mainButton.disabled) this.#mainButton.disabled = true;
    if (this.#stickyButton) this.#stickyButton.disabled = this.#mainButton.disabled;
  }

  /** @param {boolean} visible */
  #setStickyVisible(visible) {
    if (!this.#sticky || visible === this.#stickyVisible) return;
    this.#stickyVisible = visible;
    clearTimeout(this.#stickyTimer);
    cancelAnimationFrame(this.#stickyFrame);
    this.#sticky.inert = !visible;
    this.#sticky.setAttribute('aria-hidden', String(!visible));

    if (visible) {
      const wasHidden = this.#sticky.hidden;
      this.#sticky.hidden = false;
      if (this.#motion.matches || !wasHidden) {
        this.#sticky.dataset.scMotion = 'visible';
        return;
      }
      this.#sticky.dataset.scMotion = 'preparing';
      this.#stickyFrame = requestAnimationFrame(() => {
        this.#stickyFrame = requestAnimationFrame(() => {
          if (this.#active && this.#stickyVisible) this.#sticky.dataset.scMotion = 'visible';
        });
      });
      return;
    }

    if (this.#sticky.contains(document.activeElement)) this.#mainButton.focus({ preventScroll: true });
    this.#sticky.dataset.scMotion = 'leaving';
    if (this.#motion.matches || this.#sticky.hidden) {
      this.#sticky.hidden = true;
      return;
    }
    this.#stickyTimer = setTimeout(() => {
      if (this.#active && !this.#stickyVisible) this.#sticky.hidden = true;
    }, 300);
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartUpdate = (event) => {
    if (event.target !== this.#productForm || event.action !== 'add' || !event.promise || this.#pending) return;
    const request = {};
    this.#latest = request;
    this.#pending = request;
    this.#disabledBeforeRequest = this.#mainButton.disabled;
    clearTimeout(this.#confirmationTimer);
    clearTimeout(this.#feedbackTimer);
    this.#clearOwnError();
    this.#setState('loading');
    this.#syncDisabled();

    event.promise.then(({ cart, detail }) => {
      if (!this.#active || this.#pending !== request) return;
      if (!cart || detail?.didError !== false) {
        this.#failRequest(request);
        return;
      }
      this.#setState('success');
      this.#confirmationTimer = setTimeout(() => {
        if (!this.#active || this.#pending !== request) return;
        this.#restoreControls();
        this.#openCartDrawer(request);
      }, this.#motion.matches ? 0 : 180);
      this.#feedbackTimer = setTimeout(() => {
        if (this.#active && this.#latest === request && !this.#pending) this.#setState('idle');
      }, 1600);
    }).catch(() => {
      if (this.#active && this.#pending === request) this.#failRequest(request);
    });
  };

  /** @param {import('@shopify/events').CartErrorEvent} event */
  #handleCartError = (event) => {
    if (event.target === this.#productForm && this.#pending) this.#failRequest(this.#pending);
  };

  #restoreControls() {
    this.#pending = undefined;
    this.#mainButton.disabled = this.#disabledBeforeRequest;
    this.#syncDisabled();
  }

  #failRequest(request) {
    clearTimeout(this.#confirmationTimer);
    clearTimeout(this.#feedbackTimer);
    this.#restoreControls();
    this.#setState('idle');
    // Native validation writes its specific error after dispatching cart:error.
    // Supply a fallback only when that owner has no visible message.
    queueMicrotask(() => {
      if (!this.#active || this.#latest !== request || this.#pending) return;
      const error = this.#productForm.querySelector('[ref="addToCartTextError"]');
      const message = this.#template?.dataset.scError;
      if (!error || !message || !error.classList.contains('hidden')) return;
      error.textContent = message;
      error.dataset.scPurchaseError = '';
      error.classList.remove('hidden');
      const liveRegion = this.#productForm.querySelector('[ref="liveRegion"]');
      if (liveRegion) liveRegion.textContent = message;
    });
  }

  #clearOwnError() {
    const error = this.#productForm.querySelector('[data-sc-purchase-error]');
    if (!error) return;
    const liveRegion = this.#productForm.querySelector('[ref="liveRegion"]');
    if (liveRegion?.textContent === error.textContent) liveRegion.textContent = '';
    error.textContent = '';
    error.classList.add('hidden');
    delete error.dataset.scPurchaseError;
  }

  /** @param {'idle' | 'loading' | 'success'} state */
  #setState(state) {
    for (const button of this.#buttons) {
      button.dataset.scPurchaseState = state;
      if (state === 'loading') button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
  }

  async #openCartDrawer(request) {
    const cart = document.querySelector('cart-drawer-component[auto-open]');
    const drawer = cart?.closest('theme-drawer');
    if (!drawer) return;
    await customElements.whenDefined('theme-drawer');
    if (!this.#active || this.#latest !== request || !drawer.isConnected || drawer.isOpen) return;
    drawer.open();
  }

  disconnect() {
    this.#active = false;
    if (this.#productForm) delete this.#productForm.dataset.scConfirmedPurchase;
    this.#events?.abort();
    this.#purchaseObserver?.disconnect();
    this.#buttonObserver?.disconnect();
    cancelAnimationFrame(this.#stickyFrame);
    clearTimeout(this.#stickyTimer);
    clearTimeout(this.#confirmationTimer);
    clearTimeout(this.#feedbackTimer);
    if (this.#pending) this.#restoreControls();
    if (this.#mainButton) {
      if (this.#previousPuppet === null) this.#mainButton.removeAttribute('data-puppet');
      else if (this.#previousPuppet !== undefined) this.#mainButton.setAttribute('data-puppet', this.#previousPuppet);
    }
    for (const button of this.#buttons) {
      button.querySelector('.sc-purchase-feedback')?.remove();
      delete button.dataset.scPurchaseEnhanced;
      delete button.dataset.scPurchaseState;
      button.removeAttribute('aria-busy');
    }
    if (this.#sticky) {
      this.#sticky.hidden = true;
      this.#sticky.inert = false;
      this.#sticky.removeAttribute('aria-hidden');
      delete this.#sticky.dataset.scMotion;
    }
  }
}

class SecurPremiumProduct extends Component {
  #events;
  #purchase;
  #frame;
  #index = 0;
  #lastFocus;
  #overflow;
  #scrollContainer;

  connectedCallback() {
    super.connectedCallback();
    this.#events?.abort();
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    const track = this.querySelector('[data-sc-track]');
    const slides = [...this.querySelectorAll('[data-sc-slide]')];
    const thumbs = [...this.querySelectorAll('[data-sc-thumb]')];
    const status = this.querySelector('[data-sc-gallery-status]');
    const select = (index, scroll = true, focus = false) => {
      if (!track || !slides.length) return;
      this.#index = (index + slides.length) % slides.length;
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === this.#index)));
      slides.forEach((slide, i) => { slide.tabIndex = i === this.#index ? 0 : -1; slide.setAttribute('aria-hidden', String(i !== this.#index)); });
      if (status) status.textContent = status.dataset.scTemplate.replace('__SC_NUMBER__', String(this.#index + 1));
      if (scroll) track.scrollTo({ left: slides[this.#index].offsetLeft - slides[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      if (focus) slides[this.#index].focus({ preventScroll: true });
    };
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => select(i), options));
    thumbs.forEach((thumb, index) => thumb.addEventListener('keydown', (event) => {
      let next;
      const direction = getComputedStyle(thumb).direction === 'rtl' ? -1 : 1;
      if (event.key === 'ArrowRight') next = index + direction;
      if (event.key === 'ArrowLeft') next = index - direction;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = thumbs.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      const position = (next + thumbs.length) % thumbs.length;
      select(position);
      thumbs[position].focus({ preventScroll: true });
      const rail = thumbs[position].parentElement;
      rail?.scrollTo({ left: thumbs[position].offsetLeft - rail.offsetLeft - (rail.clientWidth - thumbs[position].clientWidth) / 2, behavior: 'smooth' });
    }, options));
    select(this.#index, false);
    this.querySelector('[data-sc-previous]')?.addEventListener('click', () => select(this.#index - 1), options);
    this.querySelector('[data-sc-next]')?.addEventListener('click', () => select(this.#index + 1), options);
    track?.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        const bounds = track.getBoundingClientRect();
        let closest = 0;
        let distance = Infinity;
        slides.forEach((slide, i) => {
          const delta = Math.abs(slide.getBoundingClientRect().left - bounds.left);
          if (delta < distance) { distance = delta; closest = i; }
        });
        select(closest, false);
      });
    }, options);
    track?.addEventListener('keydown', (event) => {
      if (!['ArrowLeft','ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      select(this.#index + (event.key === 'ArrowRight' ? direction : -direction), true, true);
    }, options);

    const closeDialog = (dialog) => { if (dialog instanceof HTMLDialogElement) dialog.close(); };
    const openDialog = (dialog, trigger) => {
      if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
      this.#lastFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
      const wrapper = this.closest('.page-wrapper');
      const wrapperScrolls = wrapper && wrapper.scrollHeight > wrapper.clientHeight && ['auto', 'scroll'].includes(getComputedStyle(wrapper).overflowY);
      this.#scrollContainer = wrapperScrolls ? wrapper : document.body;
      this.#overflow = this.#scrollContainer.style.overflow;
      this.#scrollContainer.style.overflow = 'hidden';
      dialog.showModal();
    };
    this.querySelectorAll('[data-sc-dialog-open]').forEach((button) => button.addEventListener('click', () => {
      openDialog(document.getElementById(button.dataset.scDialogOpen), button);
    }, options));
    this.querySelectorAll('dialog:not([data-sc-support-dialog])').forEach((dialog) => {
      dialog.addEventListener('close', () => {
        if (this.#scrollContainer) this.#scrollContainer.style.overflow = this.#overflow ?? '';
        if (this.#lastFocus instanceof HTMLElement && this.#lastFocus.isConnected) this.#lastFocus.focus({ preventScroll: true });
      }, options);
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog(dialog);
      }, options);
    });
    this.querySelectorAll('[data-sc-dialog-close]').forEach((button) => button.addEventListener('click', () => closeDialog(button.closest('dialog')), options));
    const zoomDialog = this.querySelector('[data-sc-zoom]');
    const zoomStage = zoomDialog?.querySelector('[data-sc-zoom-stage]');
    let magnified = false;
    let origin = { x: 50, y: 50 };
    let drag;
    let moved = false;
    const setOrigin = (x, y) => {
      origin = { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
      zoomStage?.style.setProperty('--sc-zoom-x', `${origin.x}%`);
      zoomStage?.style.setProperty('--sc-zoom-y', `${origin.y}%`);
    };
    const setMagnified = (value) => {
      magnified = value;
      zoomStage?.setAttribute('aria-pressed', String(value));
      zoomStage?.setAttribute('aria-label', value ? zoomStage.dataset.zoomOut : zoomStage.dataset.zoomIn);
      if (!value) setOrigin(50, 50);
    };
    zoomStage?.addEventListener('click', (event) => {
      if (moved) { moved = false; return; }
      if (!magnified && event.detail > 0) {
        const bounds = zoomStage.getBoundingClientRect();
        setOrigin((event.clientX - bounds.left) / bounds.width * 100, (event.clientY - bounds.top) / bounds.height * 100);
      }
      setMagnified(!magnified);
    }, options);
    zoomStage?.addEventListener('pointerdown', (event) => {
      moved = false;
      if (!magnified) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, origin: { ...origin } };
      zoomStage.setPointerCapture(event.pointerId);
    }, options);
    zoomStage?.addEventListener('pointermove', (event) => {
      if (!magnified) return;
      const bounds = zoomStage.getBoundingClientRect();
      if (drag && drag.id === event.pointerId) {
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 6) moved = true;
        setOrigin(drag.origin.x - dx / bounds.width * 100, drag.origin.y - dy / bounds.height * 100);
      } else if (event.pointerType === 'mouse') {
        setOrigin((event.clientX - bounds.left) / bounds.width * 100, (event.clientY - bounds.top) / bounds.height * 100);
      }
    }, options);
    const releaseDrag = () => { drag = undefined; };
    zoomStage?.addEventListener('pointerup', releaseDrag, options);
    zoomStage?.addEventListener('pointercancel', releaseDrag, options);
    zoomStage?.addEventListener('keydown', (event) => {
      if (!magnified || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      setOrigin(origin.x + (event.key === 'ArrowRight' ? 10 : event.key === 'ArrowLeft' ? -10 : 0), origin.y + (event.key === 'ArrowDown' ? 10 : event.key === 'ArrowUp' ? -10 : 0));
    }, options);
    zoomDialog?.addEventListener('close', () => { setMagnified(false); releaseDrag(); }, options);
    const zoom = (slide, trigger) => {
      const image = zoomStage?.querySelector('img');
      if (!image || !slide) return;
      setMagnified(false);
      image.src = slide.dataset.imageUrl;
      image.removeAttribute('srcset');
      image.alt = slide.querySelector('img')?.alt || '';
      openDialog(zoomDialog, trigger);
    };
    slides.forEach((slide) => slide.addEventListener('click', () => zoom(slide, slide), options));

    const form = document.getElementById(this.dataset.formId);
    const quantity = form?.querySelector('input[name="quantity"]');
    const quantityButtons = [...this.querySelectorAll('[data-sc-quantity]')];
    const refreshQuantity = () => {
      if (!(quantity instanceof HTMLInputElement)) return;
      const value = Number(quantity.value);
      const min = Number(quantity.min) || 1;
      const max = quantity.max ? Number(quantity.max) : Infinity;
      quantityButtons.forEach((button) => button.disabled = Number(button.dataset.scQuantity) < 0 ? value <= min : value >= max);
    };
    quantityButtons.forEach((button) => button.addEventListener('click', () => {
      if (!(quantity instanceof HTMLInputElement)) return;
      const step = Number(quantity.step) || 1;
      const min = Number(quantity.min) || 1;
      const max = quantity.max ? Number(quantity.max) : Infinity;
      const current = Number(quantity.value) || min;
      quantity.value = String(Math.max(min, Math.min(max, current + Number(button.dataset.scQuantity) * step)));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
      refreshQuantity();
    }, options));
    quantity?.addEventListener('input', refreshQuantity, options);
    refreshQuantity();

    this.#initializePurchase();

    this.querySelectorAll('[data-sc-delivery]').forEach((delivery) => {
      const min = Number(delivery.dataset.min), max = Number(delivery.dataset.max);
      if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < min) return;
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
      const value = (type) => parts.find((part) => part.type === type)?.value;
      const today = new Date(Date.UTC(Number(value('year')),Number(value('month')) - 1,Number(value('day')),12));
      const label = (offset) => {
        const date = new Date(today);
        date.setUTCDate(date.getUTCDate() + offset);
        return new Intl.DateTimeFormat('fr-FR',{day:'numeric',month:'short',timeZone:'Europe/Paris'}).format(date);
      };
      delivery.textContent = `${delivery.dataset.prefix} ${label(min)} ${delivery.dataset.joiner} ${label(max)}`;
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initializePurchase();
  }

  #initializePurchase() {
    this.#purchase?.disconnect();
    this.#purchase = new SecurPurchaseController(this);
    this.#purchase.connect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.querySelector('dialog:not([data-sc-support-dialog])[open]') && this.#scrollContainer) this.#scrollContainer.style.overflow = this.#overflow ?? '';
    this.#events?.abort();
    this.#purchase?.disconnect();
    cancelAnimationFrame(this.#frame);
  }
}

class SecurExperience extends Component {
  #events;
  connectedCallback() {
    super.connectedCallback();
    this.#events?.abort();
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.querySelectorAll('[data-sc-tabs]').forEach((group) => {
      const tabs = [...group.querySelectorAll('[data-sc-tab]')];
      const panels = [...group.querySelectorAll('[data-sc-panel]')];
      const select = (tab, focus = false) => {
        tabs.forEach((button) => { const selected = button === tab; button.setAttribute('aria-selected',String(selected)); button.tabIndex = selected ? 0 : -1; });
        panels.forEach((panel) => panel.hidden = panel.id !== tab.dataset.scTab);
        if (focus) tab.focus({ preventScroll: true });
      };
      tabs.forEach((tab,index) => {
        tab.addEventListener('click',() => select(tab),options);
        tab.addEventListener('keydown',(event) => {
          let position;
          if (event.key === 'ArrowRight') position = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') position = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') position = 0;
          if (event.key === 'End') position = tabs.length - 1;
          if (position === undefined) return;
          event.preventDefault();
          select(tabs[position], true);
        },options);
      });
    });
    this.querySelectorAll('[data-sc-points]').forEach((group) => {
      const buttons = [...group.querySelectorAll('[data-sc-point]')];
      const panels = [...group.querySelectorAll('[data-sc-point-panel]')];
      buttons.forEach((button) => button.addEventListener('click',() => {
        const selected = button.dataset.scPoint;
        buttons.forEach((item) => item.setAttribute('aria-pressed',String(item.dataset.scPoint === selected)));
        panels.forEach((panel) => panel.hidden = panel.dataset.scPointPanel !== selected);
      },options));
    });
  }
  disconnectedCallback() { super.disconnectedCallback(); this.#events?.abort(); }
}
if (!customElements.get('secur-premium-product')) customElements.define('secur-premium-product',SecurPremiumProduct);
if (!customElements.get('secur-experience')) customElements.define('secur-experience',SecurExperience);
